import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Edit, Store, CheckCircle, XCircle } from "lucide-react";

const B2BMarketplace = ({ user, onLogout }) => {
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    appointment_id_required: false
  });

  useEffect(() => {
    fetchMarketplaces();
  }, []);

  const fetchMarketplaces = async () => {
    try {
      const response = await axios.get(`${API}/b2b-marketplace/`);
      setMarketplaces(response.data);
    } catch (error) {
      toast.error("Failed to fetch B2B marketplaces");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMarketplace = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/b2b-marketplace/`, formData);
      toast.success("B2B Marketplace created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchMarketplaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create marketplace");
    }
  };

  const handleUpdateMarketplace = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/b2b-marketplace/${selectedMarketplace.id}`, formData);
      toast.success("B2B Marketplace updated successfully!");
      setShowEditDialog(false);
      setSelectedMarketplace(null);
      resetForm();
      fetchMarketplaces();
    } catch (error) {
      toast.error("Failed to update marketplace");
    }
  };

  const handleToggleStatus = async (marketplaceId, currentStatus) => {
    const action = currentStatus ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} this marketplace?`)) return;

    try {
      const response = await axios.patch(`${API}/b2b-marketplace/${marketplaceId}/toggle-status`);
      toast.success(response.data.message);
      fetchMarketplaces();
    } catch (error) {
      toast.error("Failed to update marketplace status");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      logo_url: "",
      appointment_id_required: false
    });
  };

  const openEditDialog = (marketplace) => {
    setSelectedMarketplace(marketplace);
    setFormData({
      name: marketplace.name,
      logo_url: marketplace.logo_url || "",
      appointment_id_required: !!marketplace.appointment_id_required
    });
    setShowEditDialog(true);
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">B2B Marketplace</h1>
            <p className="text-slate-600">Manage B2B marketplace integrations</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button style={{backgroundColor: '#00A699'}}>
                <Plus className="w-4 h-4 mr-2" />
                Add New
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add B2B Marketplace</DialogTitle>
                <DialogDescription>Create a new B2B marketplace entry</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateMarketplace} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="pr-3">
                    <Label>Appointment ID Mandatory</Label>
                    <p className="text-xs text-slate-500">Require a Delhivery Appointment ID to book appointments for this marketplace.</p>
                  </div>
                  <Switch checked={formData.appointment_id_required} onCheckedChange={(v) => setFormData({ ...formData, appointment_id_required: v })} />
                </div>
                <Button type="submit" className="w-full" style={{backgroundColor: '#00A699'}}>
                  Create Marketplace
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse flex space-x-4">
                  <div className="h-12 bg-slate-200 rounded w-full"></div>
                </div>
              ))}
            </div>
          </div>
        ) : marketplaces.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Store className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No B2B marketplaces yet</h3>
              <p className="text-slate-500 mb-6">Add your first marketplace to get started</p>
              <Button style={{backgroundColor: '#00A699'}} onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Logo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {marketplaces.map((marketplace) => (
                    <tr key={marketplace.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        {marketplace.logo_url ? (
                          <img src={marketplace.logo_url} alt={marketplace.name} className="h-10 w-10 object-contain" />
                        ) : (
                          <Store className="h-10 w-10 text-slate-300" />
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-medium text-slate-900">{marketplace.name}</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <Badge className={marketplace.is_active ? "bg-green-100 text-green-800 border-0" : "bg-red-100 text-red-800 border-0"}>
                          {marketplace.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(marketplace)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className={marketplace.is_active ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleStatus(marketplace.id, marketplace.is_active)}
                            title={marketplace.is_active ? "Disable" : "Enable"}
                          >
                            {marketplace.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit B2B Marketplace</DialogTitle>
            <DialogDescription>Update marketplace details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateMarketplace} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Name *</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_logo_url">Logo URL</Label>
              <Input
                id="edit_logo_url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="pr-3">
                <Label>Appointment ID Mandatory</Label>
                <p className="text-xs text-slate-500">Require a Delhivery Appointment ID to book appointments for this marketplace.</p>
              </div>
              <Switch checked={formData.appointment_id_required} onCheckedChange={(v) => setFormData({ ...formData, appointment_id_required: v })} />
            </div>
            <Button type="submit" className="w-full" style={{backgroundColor: '#00A699'}}>
              Update Marketplace
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2BMarketplace;